import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Search as SearchIcon, BrainCircuit, Filter, FileText, ChevronRight, TextSelect, Layers, AlertTriangle, ExternalLink, MessageSquarePlus, CheckCircle2, AlertOctagon, Info, BookOpen, Copy, ThumbsUp, ThumbsDown, LoaderCircle } from 'lucide-react';
import { useToast } from '../components/feedback/ToastProvider';
interface SearchProps {
  initialQuery?: string;
}
export function Search({
  initialQuery
}: SearchProps) {
  const [query, setQuery] = useState(initialQuery || '');
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchMode, setSearchMode] = useState<'keyword' | 'semantic' | 'hybrid'>('semantic');
  const [showFilters, setShowFilters] = useState(false);
  const [showNoAnswerState, setShowNoAnswerState] = useState(false);
  const [highlightedCitation, setHighlightedCitation] = useState<number | null>(null);
  const [hoveredCitation, setHoveredCitation] = useState<number | null>(null);
  const {
    showToast
  } = useToast();
  const SUGGESTED_QUERIES = ['What treatment did Ramirez receive after the October 2023 collision?', 'Find prior demands involving lumbar disc injury.', 'What records mention Dr. Smith?', 'Which documents discuss lost wages?', 'Summarize the driver fatigue evidence in Ramirez.'];
  const SEARCH_MODES: {
    id: 'keyword' | 'semantic' | 'hybrid';
    label: string;
    icon: typeof TextSelect;
  }[] = [{
    id: 'keyword',
    label: 'Keyword',
    icon: TextSelect
  }, {
    id: 'semantic',
    label: 'Semantic',
    icon: BrainCircuit
  }, {
    id: 'hybrid',
    label: 'Hybrid',
    icon: Layers
  }];

  // Mock data for successful answer
  const MOCK_ANSWER = {
    text: 'Following the October 12, 2023 collision, plaintiff Ramirez received immediate emergency care followed by a course of conservative orthopedic treatment and physical therapy.\n\nSpecifically, Ramirez was transported via EMS to Mercy Hospital ER on the day of the incident, where he was treated for severe L4-L5 pain and administered Morphine [1]. Two days later, on October 14, 2023, he had an initial consultation with Dr. Smith (Orthopedics), who ordered an MRI of the lumbar spine [2]. The MRI, performed on October 18, revealed an acute disc herniation at L4-L5 with nerve root impingement [3]. Subsequently, Ramirez began a 12-week course of physical therapy at PT Rehab Center, attending 24 sessions between November 2023 and January 2024 [4].',
    confidence: 94,
    answerType: 'sourced',
    citations: [{
      id: 1,
      docTitle: 'Mercy Hospital ER Records',
      bates: 'RLSF-0014',
      page: 'p. 2',
      relevance: 98,
      snippet: 'Patient admitted via EMS. Complains of severe L4-L5 pain post-collision. Administered 5mg Morphine IV. Discharged with pain management instructions.'
    }, {
      id: 2,
      docTitle: 'Dr. Smith Orthopedic Consultation Notes',
      bates: 'RLSF-0056',
      page: 'p. 1',
      relevance: 95,
      snippet: 'Initial consult. MRI ordered for lumbar spine to evaluate radicular symptoms following MVA. Patient reports numbness in left foot.'
    }, {
      id: 3,
      docTitle: 'Mercy Imaging MRI Report',
      bates: 'RLSF-0074',
      page: 'p. 1',
      relevance: 99,
      snippet: 'Findings: Acute disc herniation at L4-L5 with left-sided nerve root impingement. No prior imaging available for comparison.'
    }, {
      id: 4,
      docTitle: 'PT Rehab Center Progress Notes',
      bates: 'RLSF-0095',
      page: 'p. 12',
      relevance: 88,
      snippet: 'Patient completed 24/24 prescribed PT sessions for lumbar stabilization. Reports pain level decreased from 8/10 to 4/10.'
    }],
    inferredStatements: [{
      statement: 'The 12-week duration is calculated from the first PT visit (Nov 2, 2023) to the final progress note (Jan 24, 2024).',
      confidence: 85
    }],
    relatedQuestions: ['Did Ramirez report any prior back pain before the collision?', 'What were the total medical expenses for the Mercy Hospital visit?', 'Has Dr. Smith recommended surgical intervention?']
  };

  // Mock data for insufficient evidence state
  const MOCK_NO_ANSWER = {
    query: 'What was the exact speed of the truck at impact?',
    reason: 'The corpus does not contain sufficient evidence to answer this question with confidence.',
    partialFindings: [{
      finding: 'The police report mentions "high rate of speed" but does not specify an exact figure.',
      source: 'Police Accident Report',
      bates: 'RLSF-0115',
      confidence: 45
    }, {
      finding: 'Dr. Allen\'s accident reconstruction report references "estimated impact velocity" but the specific page was not successfully OCR\'d (quality: 42%).',
      source: 'Expert Report — Dr. Allen',
      bates: 'RLSF-0312',
      confidence: 32
    }],
    suggestedActions: ['Request re-scan of Dr. Allen expert report pages 8-12', "Subpoena truck's EDR (Event Data Recorder) data", 'Review deposition transcripts for speed testimony']
  };
  const MOCK_DOCUMENTS = [{
    id: 'doc1',
    title: 'Mercy Hospital ER Records',
    type: 'Medical Record',
    date: 'Oct 12, 2023',
    relevance: 98,
    bates: 'RLSF-0013 — 0054',
    excerpt: "...Patient admitted via EMS. Complains of <mark class='bg-yellow-100 text-yellow-900 rounded px-0.5'>severe L4-L5 pain</mark> post-collision. Administered 5mg Morphine IV..."
  }, {
    id: 'doc2',
    title: 'Mercy Imaging MRI Report',
    type: 'Medical Record',
    date: 'Oct 18, 2023',
    relevance: 95,
    bates: 'RLSF-0073 — 0078',
    excerpt: "...Findings: <mark class='bg-yellow-100 text-yellow-900 rounded px-0.5'>Acute disc herniation at L4-L5</mark> with left-sided nerve root impingement..."
  }, {
    id: 'doc3',
    title: 'Dr. Smith Orthopedic Consultation Notes',
    type: 'Medical Record',
    date: 'Oct 14, 2023',
    relevance: 92,
    bates: 'RLSF-0055 — 0072',
    excerpt: "...Initial consult. <mark class='bg-yellow-100 text-yellow-900 rounded px-0.5'>MRI ordered for lumbar spine</mark> to evaluate radicular symptoms following MVA..."
  }, {
    id: 'doc4',
    title: 'PT Rehab Center Progress Notes',
    type: 'Medical Record',
    date: 'Jan 24, 2024',
    relevance: 85,
    bates: 'RLSF-0079 — 0110',
    excerpt: "...Patient completed 24/24 prescribed PT sessions for <mark class='bg-yellow-100 text-yellow-900 rounded px-0.5'>lumbar stabilization</mark>..."
  }, {
    id: 'doc5',
    title: "Plaintiff's Responses to Interrogatories",
    type: 'Discovery',
    date: 'Dec 15, 2023',
    relevance: 78,
    bates: 'RLSF-DISC-0012',
    excerpt: "...INTERROGATORY NO. 8: Describe all <mark class='bg-yellow-100 text-yellow-900 rounded px-0.5'>medical treatment received</mark> as a result of the incident..."
  }];
  const completeSearch = (searchQuery: string) => {
    const noAnswer = searchQuery.toLowerCase().includes('speed');
    setShowNoAnswerState(noAnswer);
    setIsSearching(false);
    setHasSearched(true);
    showToast({
      title: noAnswer ? 'Search completed with evidence gaps' : 'Grounded answer ready',
      description: noAnswer ? 'The current corpus could not support a high-confidence answer for this query.' : `Results were synthesized in ${searchMode} mode with linked citations.`,
      variant: noAnswer ? 'info' : 'success'
    });
  };
  const handleSearch = (e?: React.FormEvent, searchQuery?: string) => {
    if (e) e.preventDefault();
    const q = searchQuery || query;
    if (!q.trim()) return;
    setQuery(q);
    setIsSearching(true);
    setHasSearched(false);
    setTimeout(() => {
      completeSearch(q);
    }, 1500);
  };

  // Auto-run search when an initial query is passed in (e.g., from the global header search)
  useEffect(() => {
    const q = initialQuery?.trim();
    if (!q) return;
    setQuery(q);
    setIsSearching(true);
    setHasSearched(false);
    const timer = setTimeout(() => {
      completeSearch(q);
    }, 1500);
    return () => clearTimeout(timer);
  }, [initialQuery]);
  return <div className="space-y-6 pb-12" data-id="element-2112">
    {/* Page Header */}
    <div className="flex flex-col space-y-2" data-id="element-2113">
      <div className="flex items-center space-x-3" data-id="element-2114">
        <div className="p-2 bg-blue-100 text-blue-700 rounded-lg" data-id="element-2115">
          <BrainCircuit className="w-6 h-6" data-id="element-2116" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight" data-id="element-2117">
            Search & Grounded Answers
          </h1>
        </div>
        <p className="text-muted-foreground" data-id="element-2118">
          Query the local corpus. All answers are grounded in indexed documents
          with verifiable citations.
        </p>
      </div>

      {/* Search Bar Area */}
      <div className="space-y-4" data-id="element-2119">
        <form onSubmit={handleSearch} className="relative" data-id="element-2120">
          <div className="relative flex items-center" data-id="element-2121">
            <SearchIcon className="absolute left-4 w-5 h-5 text-muted-foreground" data-id="element-2122" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} className="w-full h-14 pl-12 pr-32 rounded-xl border-2 border-input bg-background text-lg shadow-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" placeholder="Ask about a matter, document set, provider, or issue..." data-id="element-2123" />

            <div className="absolute right-2 flex space-x-2" data-id="element-2124">
              <Button type="button" variant={showFilters ? 'secondary' : 'ghost'} size="sm" className="h-10 text-muted-foreground" onClick={() => setShowFilters(!showFilters)} data-id="element-2125">

                <Filter className="w-4 h-4" data-id="element-2126" />
              </Button>
              <Button type="submit" className="h-10 px-6" disabled={isSearching || !query.trim()} data-id="element-2127">
                {isSearching ? <>
                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" data-id="element-2128" />
                    Searching
                  </> : 'Search'}
              </Button>
            </div>
          </div>
        </form>

        {/* Search Mode Toggle & Suggested Queries */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2129">
          <div className="inline-flex flex-wrap gap-1 bg-muted p-1 rounded-lg" role="tablist" aria-label="Search mode" data-id="element-2130">
            {SEARCH_MODES.map(mode => {
            const Icon = mode.icon;
            const isActive = searchMode === mode.id;
            return <button key={mode.id} type="button" role="tab" aria-selected={isActive} onClick={() => setSearchMode(mode.id)} className={`flex items-center space-x-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-background hover:text-foreground'}`} data-id="element-2131">
                  <Icon className="w-4 h-4" data-id="element-2132" />
                  <span data-id="element-2133">{mode.label}</span>
                </button>;
          })}
          </div>

          {!hasSearched && !isSearching && <div className="flex flex-wrap gap-2" data-id="element-2134">
              {SUGGESTED_QUERIES.slice(0, 3).map((q, i) => <button key={i} onClick={() => handleSearch(undefined, q)} className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full transition-colors border border-blue-200" data-id="element-2135">

                  {q}
                </button>)}
            </div>}
        </div>

        {/* Collapsible Filter Bar */}
        {showFilters && <Card className="bg-muted/30 border-dashed animate-in slide-in-from-top-2 duration-200" data-id="element-2136">
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-2137">
              <div className="space-y-1.5" data-id="element-2138">
                <label className="text-xs font-medium text-muted-foreground" data-id="element-2139">
                  Matter
                </label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-2140">
                  <option data-id="element-2141">All Matters</option>
                  <option data-id="element-2142">Ramirez v. Lone Star Freight</option>
                  <option data-id="element-2143">Castillo v. Westfield Properties</option>
                  <option data-id="element-2144">Ortega v. Gulf Coast Industrial</option>
                  <option data-id="element-2145">Garcia v. Metro Transit Authority</option>
                  <option data-id="element-2146">Nguyen v. St. Luke's Medical Center</option>
                </select>
              </div>
              <div className="space-y-1.5" data-id="element-2147">
                <label className="text-xs font-medium text-muted-foreground" data-id="element-2148">
                  Document Type
                </label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-2149">
                  <option data-id="element-2150">All Types</option>
                  <option data-id="element-2151">Medical Records</option>
                  <option data-id="element-2152">Pleadings</option>
                  <option data-id="element-2153">Discovery Responses</option>
                  <option data-id="element-2154">Depositions</option>
                  <option data-id="element-2155">Expert Reports</option>
                  <option data-id="element-2156">Correspondence</option>
                </select>
              </div>
              <div className="space-y-1.5" data-id="element-2157">
                <label className="text-xs font-medium text-muted-foreground" data-id="element-2158">
                  Date Range
                </label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-2159">
                  <option data-id="element-2160">Any Time</option>
                  <option data-id="element-2161">Last 7 Days</option>
                  <option data-id="element-2162">Last 30 Days</option>
                  <option data-id="element-2163">Last 90 Days</option>
                  <option data-id="element-2164">Last Year</option>
                </select>
              </div>
              <div className="space-y-1.5" data-id="element-2165">
                <label className="text-xs font-medium text-muted-foreground" data-id="element-2166">
                  Source
                </label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-2167">
                  <option data-id="element-2168">All Sources</option>
                  <option data-id="element-2169">Local Upload</option>
                  <option data-id="element-2170">Email Import</option>
                  <option data-id="element-2171">Dropbox</option>
                  <option data-id="element-2172">iCloud</option>
                </select>
              </div>
              <div className="space-y-1.5" data-id="element-2173">
                <label className="text-xs font-medium text-muted-foreground" data-id="element-2174">
                  OCR Quality
                </label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-2175">
                  <option data-id="element-2176">Any Quality</option>
                  <option data-id="element-2177">High (&gt;90%)</option>
                  <option data-id="element-2178">Medium (70-90%)</option>
                  <option data-id="element-2179">Low (&lt;70%)</option>
                </select>
              </div>
              <div className="space-y-1.5" data-id="element-2180">
                <label className="text-xs font-medium text-muted-foreground" data-id="element-2181">
                  Review State
                </label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-2182">
                  <option data-id="element-2183">Any State</option>
                  <option data-id="element-2184">Pending Review</option>
                  <option data-id="element-2185">Approved</option>
                  <option data-id="element-2186">Flagged</option>
                  <option data-id="element-2187">Privileged</option>
                </select>
              </div>
            </CardContent>
          </Card>}
      </div>

      {/* Loading State */}
      {isSearching && <div className="py-24 flex flex-col items-center justify-center space-y-6 text-muted-foreground" data-id="element-2188">
          <div className="relative" data-id="element-2189">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" data-id="element-2190"></div>
            <BrainCircuit className="w-5 h-5 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" data-id="element-2191" />
          </div>
          <div className="text-center space-y-2" data-id="element-2192">
            <p className="text-lg font-medium text-foreground" data-id="element-2193">
              Synthesizing Answer...
            </p>
            <p className="text-sm" data-id="element-2194">
              Scanning 14,205 documents in the local corpus.
            </p>
          </div>
        </div>}

      {/* NO ANSWER FOUND STATE */}
      {hasSearched && !isSearching && showNoAnswerState && <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2195">
          <Card className="border-red-200 bg-red-50/30 shadow-md" data-id="element-2196">
            <CardHeader className="pb-3 border-b border-red-100" data-id="element-2197">
              <div className="flex items-start space-x-3" data-id="element-2198">
                <div className="p-2 bg-red-100 rounded-lg" data-id="element-2199">
                  <AlertOctagon className="w-6 h-6 text-red-600" data-id="element-2200" />
                </div>
                <div className="flex-1" data-id="element-2201">
                  <CardTitle className="text-lg text-red-900" data-id="element-2202">
                    Insufficient Evidence to Answer
                  </CardTitle>
                  <CardDescription className="text-red-700 mt-1" data-id="element-2203">
                    The system could not find sufficient supporting
                    documentation to provide a reliable answer to this query.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6" data-id="element-2204">
              <div className="bg-white rounded-lg border border-red-100 p-4" data-id="element-2205">
                <p className="text-sm font-medium text-foreground mb-2" data-id="element-2206">
                  Query:
                </p>
                <p className="text-foreground italic" data-id="element-2207">
                  "{MOCK_NO_ANSWER.query}"
                </p>
              </div>

              <div data-id="element-2208">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center" data-id="element-2209">
                  <Info className="w-4 h-4 mr-2 text-muted-foreground" data-id="element-2210" />
                  Partial Findings (Low Confidence)
                </h4>
                <div className="space-y-3" data-id="element-2211">
                  {MOCK_NO_ANSWER.partialFindings.map((finding, i) => <div key={i} className="bg-amber-50/50 border border-amber-200 rounded-lg p-4" data-id="element-2212">

                      <div className="flex items-start justify-between mb-2" data-id="element-2213">
                        <div className="flex items-center space-x-2" data-id="element-2214">
                          <Badge variant="outline" className="text-[10px] bg-white font-mono" data-id="element-2215">

                            {finding.bates}
                          </Badge>
                          <span className="text-xs text-muted-foreground" data-id="element-2216">
                            {finding.source}
                          </span>
                        </div>
                        <Badge variant="destructive" className="text-[10px]" data-id="element-2217">
                          {finding.confidence}% Conf.
                        </Badge>
                      </div>
                      <p className="text-sm text-foreground" data-id="element-2218">
                        {finding.finding}
                      </p>
                    </div>)}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2219">
                <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center" data-id="element-2220">
                  <BookOpen className="w-4 h-4 mr-2" data-id="element-2221" />
                  Suggested Actions to Resolve
                </h4>
                <ul className="space-y-2" data-id="element-2222">
                  {MOCK_NO_ANSWER.suggestedActions.map((action, i) => <li key={i} className="flex items-start text-sm text-blue-800" data-id="element-2223">

                      <ChevronRight className="w-4 h-4 mr-2 text-blue-500 shrink-0 mt-0.5" data-id="element-2224" />
                      {action}
                    </li>)}
                </ul>
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-center text-muted-foreground flex items-center justify-center" data-id="element-2225">
            <AlertTriangle className="w-3 h-3 mr-1.5" data-id="element-2226" />
            This query returned insufficient evidence. Consider refining your
            search or reviewing the suggested actions above.
          </p>
        </div>}

      {/* SUCCESSFUL ANSWER STATE */}
      {hasSearched && !isSearching && !showNoAnswerState && <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2227">
          {/* Left Pane: Ranked Documents */}
          <div className="lg:col-span-5 space-y-4" data-id="element-2228">
            <div className="flex items-center justify-between pb-2 border-b" data-id="element-2229">
              <h3 className="font-semibold text-lg flex items-center" data-id="element-2230">
                <FileText className="w-5 h-5 mr-2 text-muted-foreground" data-id="element-2231" />
                Source Documents
              </h3>
              <Badge variant="secondary" data-id="element-2232">{MOCK_DOCUMENTS.length} Results</Badge>
            </div>

            <div className="space-y-3" data-id="element-2233">
              {MOCK_DOCUMENTS.map((doc, index) => <Card key={doc.id} className="hover:border-blue-300 transition-colors cursor-pointer group shadow-sm" data-id="element-2234">

                  <CardContent className="p-4" data-id="element-2235">
                    <div className="flex items-start justify-between mb-2" data-id="element-2236">
                      <div className="flex items-start space-x-3" data-id="element-2237">
                        <div className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 group-hover:bg-blue-100 group-hover:text-blue-700 transition-colors" data-id="element-2238">
                          {index + 1}
                        </div>
                        <div data-id="element-2239">
                          <h4 className="font-medium text-sm group-hover:text-blue-600 transition-colors line-clamp-1" title={doc.title} data-id="element-2240">

                            {doc.title}
                          </h4>
                          <div className="flex items-center space-x-2 text-xs text-muted-foreground mt-1" data-id="element-2241">
                            <span data-id="element-2242">{doc.type}</span>
                            <span data-id="element-2243">•</span>
                            <span data-id="element-2244">{doc.date}</span>
                          </div>
                        </div>
                      </div>
                      <Badge variant={doc.relevance > 90 ? 'success' : 'secondary'} className="text-[10px] shrink-0 ml-2" data-id="element-2245">

                        {doc.relevance}%
                      </Badge>
                    </div>

                    <div className="mt-3 text-xs text-muted-foreground bg-muted/30 p-2.5 rounded border border-border/50" data-id="element-2246">
                      <div className="font-mono text-[10px] text-blue-600 font-medium mb-1" data-id="element-2247">
                        {doc.bates}
                      </div>
                      <p className="italic leading-relaxed" dangerouslySetInnerHTML={{
                  __html: doc.excerpt
                }} data-id="element-2248">
                  </p>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>

          {/* Right Pane: Grounded Answer */}
          <div className="lg:col-span-7 space-y-6" data-id="element-2249">
            {/* Main Answer Card */}
            <Card className="border-blue-200 shadow-lg relative overflow-hidden" data-id="element-2250">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-blue-500" data-id="element-2251"></div>
              <CardHeader className="pb-3 bg-blue-50/50 border-b border-blue-100" data-id="element-2252">
                <div className="flex items-start justify-between" data-id="element-2253">
                  <div className="flex items-center space-x-2" data-id="element-2254">
                    <BrainCircuit className="w-5 h-5 text-blue-600" data-id="element-2255" />
                    <CardTitle className="text-lg" data-id="element-2256">Grounded Answer</CardTitle>
                  </div>
                  <div className="flex items-center space-x-3" data-id="element-2257">
                    <div className="flex items-center space-x-1.5" data-id="element-2258">
                      <span className="text-xs text-muted-foreground" data-id="element-2259">
                        Confidence:
                      </span>
                      <Badge variant="success" className="flex items-center space-x-1" data-id="element-2260">

                        <CheckCircle2 className="w-3 h-3" data-id="element-2261" />
                        <span data-id="element-2262">{MOCK_ANSWER.confidence}%</span>
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6" data-id="element-2263">
                {/* Answer Type Indicator */}
                <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start space-x-3" data-id="element-2264">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" data-id="element-2265" />
                  <div data-id="element-2266">
                    <p className="text-sm font-medium text-emerald-900" data-id="element-2267">
                      Fully Sourced Answer
                    </p>
                    <p className="text-xs text-emerald-700 mt-0.5" data-id="element-2268">
                      All factual claims in this answer are directly supported
                      by citations to indexed documents.
                    </p>
                  </div>
                </div>

                {/* Answer Text with Citations */}
                <div className="prose prose-sm max-w-none text-foreground leading-relaxed whitespace-pre-wrap" data-id="element-2269">
                {MOCK_ANSWER.text.split(/(\[\d+\])/).map((part, i) => {
                if (part.match(/\[\d+\]/)) {
                  const citationNum = part.replace(/[[\]]/g, '');
                  const citationData = MOCK_ANSWER.citations.find(c => c.id === parseInt(citationNum));
                  const isHighlighted = highlightedCitation === parseInt(citationNum);
                  const isHovered = hoveredCitation === parseInt(citationNum);
                  return <sup key={i} className="mx-0.5" data-id="element-2270">
                          <a href={`#citation-${citationNum}`} title={citationData ? `[${citationNum}] ${citationData.docTitle} — ${citationData.bates}` : `Citation ${citationNum}`} className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold transition-all no-underline cursor-pointer ${isHighlighted ? 'bg-amber-500 text-white hover:bg-amber-600 hover:ring-2 hover:ring-amber-300' : isHovered ? 'bg-blue-700 text-white hover:bg-blue-800' : 'bg-blue-600 text-white hover:bg-blue-700'} hover:ring-2 hover:ring-blue-300 hover:ring-offset-1`} onMouseEnter={() => setHoveredCitation(parseInt(citationNum))} onMouseLeave={() => setHoveredCitation(null)} onClick={() => setHighlightedCitation(highlightedCitation === parseInt(citationNum) ? null : parseInt(citationNum))} data-id="element-2271">
                            {citationNum}
                          </a>
                        </sup>;
                }
                return <span key={i} data-id="element-2272">{part}</span>;
              })}
                </div>

                {/* Inferred Statements Warning */}
                {MOCK_ANSWER.inferredStatements.length > 0 && <div className="mt-6 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-2273">
                    <div className="flex items-start space-x-2" data-id="element-2274">
                      <AlertTriangle className="w-4 h-4 text-amber-600" data-id="element-2275" />
                      <div data-id="element-2276">
                        <p className="text-xs font-semibold text-amber-900 uppercase tracking-wider mb-1" data-id="element-2277">
                          Inferred Statement
                        </p>
                        {MOCK_ANSWER.inferredStatements.map((stmt, i) => <div key={i} className="flex items-center flex-wrap gap-2 text-sm text-amber-800" data-id="element-2278">
                            <span data-id="element-2279">{stmt.statement}</span>
                            <Badge variant="outline" className="ml-1 text-[10px] bg-white shrink-0" data-id="element-2280">

                              {stmt.confidence}% conf.
                            </Badge>
                          </div>)}
                      </div>
                    </div>
                  </div>}
              </CardContent>
              <CardFooter className="p-4 border-t bg-muted/20 flex items-center justify-between" data-id="element-2281">
                <div className="flex items-center space-x-2 text-xs text-muted-foreground" data-id="element-2282">
                  <span data-id="element-2283">Was this answer helpful?</span>
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => showToast({
                title: 'Feedback captured',
                description: 'This answer was marked as helpful for future tuning.',
                variant: 'success'
              })} data-id="element-2284">
                    <ThumbsUp className="w-3 h-3" data-id="element-2285" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => showToast({
                title: 'Feedback captured',
                description: 'The answer was flagged for improvement and attorney follow-up.',
                variant: 'info'
              })} data-id="element-2286">
                    <ThumbsDown className="w-3 h-3" data-id="element-2287" />
                  </Button>
                </div>
                <div className="flex space-x-2" data-id="element-2288">
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => showToast({
                title: 'Answer copied',
                description: 'The grounded answer was copied to your clipboard buffer.',
                variant: 'success'
              })} data-id="element-2289">

                    <Copy className="w-3 h-3 mr-1" data-id="element-2290" /> Copy
                  </Button>
                </div>
              </CardFooter>
            </Card>

            {/* Supporting Citations Panel */}
            <Card className="shadow-sm" data-id="element-2291">
              <CardHeader className="py-3 border-b bg-muted/20" data-id="element-2292">
                <CardTitle className="text-sm flex items-center" data-id="element-2293">
                  <BookOpen className="w-4 h-4 mr-2 text-muted-foreground" data-id="element-2294" />
                  Citation Details ({MOCK_ANSWER.citations.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0" data-id="element-2295">
                <div className="divide-y" data-id="element-2296">
                  {MOCK_ANSWER.citations.map(citation => {
                const isHighlighted = highlightedCitation === citation.id;
                const isHovered = hoveredCitation === citation.id;
                return <div key={citation.id} id={`citation-${citation.id}`} className={`p-4 transition-colors group ${isHighlighted ? 'bg-amber-50 border-amber-300' : isHovered ? 'bg-blue-50 border-blue-200' : 'hover:bg-muted/30'}`} data-id="element-2297">
                      <div className="flex items-start space-x-3" data-id="element-2298">
                        <div className={`w-6 h-6 rounded text-white flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 transition-colors ${isHighlighted ? 'bg-amber-600 hover:bg-amber-700' : isHovered ? 'bg-blue-700 hover:bg-blue-800' : 'bg-blue-600 hover:bg-blue-700'}`} data-id="element-2299">
                          {citation.id}
                        </div>
                        <div className="flex-1 space-y-2" data-id="element-2300">
                          <div className="flex items-start justify-between" data-id="element-2301">
                            <div data-id="element-2302">
                              <h5 className={`text-sm font-medium flex items-center transition-colors ${isHighlighted ? 'text-amber-900' : isHovered ? 'text-blue-900' : 'text-foreground'} ${isHighlighted || isHovered ? 'group-hover:' : ''}hover:text-blue-600`} data-id="element-2303">
                                {citation.docTitle}
                                <ExternalLink className={`w-3 h-3 ml-1.5 opacity-0 transition-opacity ${isHighlighted || isHovered ? 'opacity-100' : 'group-hover:opacity-100'}`} data-id="element-2304" />
                              </h5>
                              <div className="flex items-center space-x-2 mt-0.5" data-id="element-2305">
                                <span className={`text-xs font-mono font-medium transition-colors ${isHighlighted ? 'text-amber-700' : isHovered ? 'text-blue-700' : 'text-blue-600'}`} data-id="element-2306">
                                  {citation.bates}
                                </span>
                                <span className="text-xs text-muted-foreground" data-id="element-2307">
                                  {citation.page}
                                </span>
                              </div>
                            </div>
                            <Badge variant="outline" className={`text-[10px] transition-colors ${isHighlighted ? 'bg-amber-100 text-amber-700 border-amber-300' : isHovered ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-white'}`} data-id="element-2308">
                              {citation.relevance}% match
                            </Badge>
                          </div>
                          <div className={`text-sm bg-muted/50 p-3 rounded border-l-2 italic transition-colors ${isHighlighted ? 'border-amber-400 text-amber-900 bg-amber-50' : isHovered ? 'border-blue-400 text-blue-900 bg-blue-50' : 'border-blue-300 text-foreground'}`} data-id="element-2309">
                            "{citation.snippet}"
                          </div>
                        </div>
                      </div>
                    </div>;
              })}
                </div>
              </CardContent>
              <CardFooter className="p-3 border-t bg-muted/20" data-id="element-2310">
                <div className="flex items-center justify-between" data-id="element-2311">
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                setHighlightedCitation(null);
                showToast({
                  title: 'Highlight cleared',
                  description: 'Citation highlighting has been reset.',
                  variant: 'info'
                });
              }} data-id="element-2312">
                    Clear Highlight
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs" data-id="element-2313">
                    <ExternalLink className="w-3 h-3 mr-2" data-id="element-2314" />
                    Open All Cited Documents in Workspace
                  </Button>
                </div>
              </CardFooter>
            </Card>

            {/* Related Questions */}
            <Card className="shadow-sm border-dashed" data-id="element-2315">
              <CardHeader className="py-3 border-b" data-id="element-2316">
                <CardTitle className="text-sm flex items-center text-muted-foreground" data-id="element-2317">
                  <MessageSquarePlus className="w-4 h-4 mr-2" data-id="element-2318" />
                  Follow-up Questions
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3" data-id="element-2319">
                <div className="flex flex-col space-y-2" data-id="element-2320">
                  {MOCK_ANSWER.relatedQuestions.map((q, i) => <button key={i} onClick={() => handleSearch(undefined, q)} className="text-left text-sm text-blue-600 hover:text-blue-800 hover:underline py-1.5 px-2 rounded hover:bg-blue-50 transition-colors flex items-center" data-id="element-2321">

                      <ChevronRight className="w-3 h-3 mr-1.5 text-blue-400" data-id="element-2322" />
                      {q}
                    </button>)}
                </div>
              </CardContent>
            </Card>

            {/* Disclaimer */}
            <p className="text-xs text-center text-muted-foreground flex items-center justify-center" data-id="element-2323">
              <AlertTriangle className="w-3 h-3 mr-1.5" data-id="element-2324" />
              AI-assisted results require attorney review. Confidence scores
              reflect document relevance, not legal accuracy.
            </p>
          </div>
        </div>}
    </div>;
}